import { useState } from 'react'

const sections = [
  {
    id: 'welcome',
    title: '1. Welcome Screen',
    points: [
      'When you first open the app, you\'ll see the welcome screen with the app logo and features overview.',
      'Tap the opening prayer play button to listen to the opening prayer (mobile only).',
      'Tap "Start Learning" to enter the app.',
    ],
  },
  {
    id: 'language',
    title: '2. Language & Translation',
    points: [
      'The app supports three translation languages: Amharic, Tigrinya, and Latin — all in one unified app.',
      'Use the language tabs at the top of the screen to switch between translations at any time.',
      'The Ge\'ez liturgical text and English translation are always shown. The middle translation changes based on your selected language.',
      'Your language choice is remembered and applied automatically on your next visit.',
    ],
  },
  {
    id: 'categories',
    title: '3. Categories',
    points: [
      'The main screen shows liturgy categories: Sirate Kidase and Kidase Hawariat, each in Geez and Ezli versions.',
      'Each category shows the total number of lessons and your progress.',
      'Tap a category to open its lessons.',
    ],
  },
  {
    id: 'roles',
    title: '4. Role Filtering',
    points: [
      'Lessons are tagged by liturgical role: Priest (ካህን), Asst. Priest (ረ.ካህን), Deacon (ዲያቆን), and People (ሕዝብ).',
      'Use the role filter buttons at the top of the lesson list to show only lessons for a specific role.',
      'Select "All" to see every lesson in the category.',
      'Each filter button shows the number of lessons for that role.',
    ],
  },
  {
    id: 'lessons',
    title: '5. Lessons',
    points: [
      'Each lesson shows a number and title (first two Ge\'ez words), e.g., "8. አምነ በሃ".',
      'The lesson displays Ge\'ez text (top), translation in your selected language (middle), and English translation (bottom).',
      'Role badges show who chants each part: Priest, Asst. Priest, People, Deacon, or Together.',
      'Tap a lesson to expand it and see the audio player.',
      'The first 7 lessons in each category are free. Kidase Premium is required for the remaining lessons.',
    ],
  },
  {
    id: 'audio',
    title: '6. Audio Playback',
    points: [
      'Tap the play button to listen to the lesson audio.',
      'The progress bar shows playback position — tap anywhere on it to jump to that point.',
      'Use the skip buttons to move to the previous or next word.',
      'Words in the Ge\'ez text highlight in sync with the audio as it plays.',
      'Tap any Ge\'ez word to jump to that point in the audio.',
    ],
  },
  {
    id: 'recording',
    title: '7. Recording Your Voice',
    points: [
      'Tap "Record" to record yourself chanting the lesson.',
      'The app will ask for microphone permission the first time.',
      'A timer shows how long you\'ve been recording. Tap "Stop" when done.',
      'After recording, play back your recording to review it.',
      'On mobile, use the share button to share your recording with the lesson text.',
      'On desktop, use the download button to save your recording as an audio file.',
      'Tap delete to remove your recording and try again.',
    ],
  },
  {
    id: 'sharing',
    title: '8. Sharing',
    points: [
      'Sharing is available on mobile devices. Tap the share icon on any lesson to open the native share sheet (WhatsApp, Telegram, Email, etc.).',
      'If you have a recording, it will be shared along with the lesson text.',
      'If no recording exists, the text is shared with a "No recorded audio" note.',
      'On desktop, use the download button to save your recording as an audio file.',
    ],
  },
  {
    id: 'signin',
    title: '9. Sign In',
    points: [
      'Google sign-in is free. It does not include Premium; sign in with the account that has your active subscription or trial.',
      'Tap the sign-in prompt that appears when you try to open a locked lesson, or use the sign-in button.',
      'Approved tester accounts also have access to all lessons.',
    ],
  },
  {
    id: 'settings',
    title: '10. Settings',
    points: [
      'Tap the gear icon on the categories screen to open Settings.',
      'Font Size — adjust the Ge\'ez text size using the slider.',
      'Dark Mode — toggle between light and dark themes.',
      'Credits — view developer and audio credits.',
    ],
  },
]

export default function Guide({ onBack }) {
  const [activeSection, setActiveSection] = useState(null)

  return (
    <div className="guide-page">
      <div className="guide-header">
        <button className="guide-back-btn" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1 className="guide-title">User Guide</h1>
      </div>

      <div className="guide-intro">
        <p>Welcome to <strong>Sirate Kidase Tutor</strong> — your companion for learning Ethiopian Orthodox liturgical chanting.</p>
      </div>

      <div className="guide-sections">
        {sections.map(section => (
          <div key={section.id} className="guide-section">
            <button
              className={`guide-section-header ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
            >
              <span className="guide-section-title">{section.title}</span>
              <svg
                className={`guide-chevron ${activeSection === section.id ? 'open' : ''}`}
                width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {activeSection === section.id && (
              <div className="guide-section-content">
                <ul className="guide-points">
                  {section.points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="guide-footer">
        <p><strong>Developer:</strong> Yemane Russom</p>
        <p><strong>Kidase Audio:</strong> Kesis Yohannes Negassi</p>
      </div>
    </div>
  )
}
